import { DOLLARS_SAVED_PER_HOUR } from '@common/constants';
import { Alert, Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import PageWrapper from '@/components/PageWrapper';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useDashboard } from '@/queries/insights';
import useIdentityStore from '@/stores/useIdentityStore';
import DayOfWeekBars from './DayOfWeekBars';
import HeroCard from './HeroCard';
import NeedsAttentionStrip from './NeedsAttentionStrip';

function humanizeMinutes(minutes: number | null): string {
    if (minutes == null) return '—';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = minutes / 60;
    if (hours < 48) return `${hours.toFixed(1)} hours`;
    const days = hours / 24;
    return `${days.toFixed(1)} days`;
}

function buildTimeSavedSubtitle(timeSavedHours: number): string {
    const rate = `$${DOLLARS_SAVED_PER_HOUR.toFixed(2)}/hr`;
    if (timeSavedHours < 8) return `Based on ${rate} · 2 hrs per completed intake`;
    const days = Math.round(timeSavedHours / 8);
    if (days < 10)
        return `≈ ${days} working ${days === 1 ? 'day' : 'days'} back · based on ${rate}`;
    const weeks = Math.round(days / 5);
    return `≈ ${weeks} work ${weeks === 1 ? 'week' : 'weeks'} back · based on ${rate}`;
}

function buildMomentumLabel(value: number, suffix: string): string | null {
    if (value === 0) return null;
    return `+${value.toLocaleString()} ${suffix} this month`;
}

export default function Dashboard() {
    const theme = useTheme();
    const navigate = useNavigate();
    const loggedInUser = useIdentityStore((state) => state.user);
    const { isSuperAdmin, activeLawFirmId } = useLawFirmContext();

    const { data, isLoading, isError, refetch } = useDashboard(activeLawFirmId);

    if (loggedInUser && !isSuperAdmin) {
        return <Navigate to="/prospects" replace />;
    }

    const timeSavedHours = data ? Math.floor(data.allTime.timeSavedIntakeMinutes / 60) : 0;
    const timeSavedDollars = data
        ? Math.round((data.allTime.timeSavedIntakeMinutes / 60) * DOLLARS_SAVED_PER_HOUR)
        : 0;
    const isNewFirm = data != null && data.allTime.intakesSent === 0;
    const showNeedsAttention = data != null && data.allTime.intakesSent > 0;
    const hasInsightData = data != null && data.trailing90.intakesCompleted >= 5;

    return (
        <PageWrapper title="Dashboard">
            {isError && (
                <Alert
                    severity="error"
                    action={
                        <Button size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                >
                    Failed to load dashboard data.
                </Alert>
            )}

            {/* Hero row */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} xl={3}>
                    <HeroCard
                        title="TIME SAVED WITH LEXO"
                        value={isLoading ? null : timeSavedHours}
                        unit="hours"
                        secondaryValue={isLoading ? null : timeSavedDollars}
                        secondaryUnit="saved"
                        secondaryFormat="currency"
                        subtitle={buildTimeSavedSubtitle(timeSavedHours)}
                        tooltip={`We estimate each completed client intake saves your firm 2 hours of back-and-forth, valued at $${DOLLARS_SAVED_PER_HOUR.toFixed(2)}/hr.`}
                        milestoneType="hours"
                        momentumLabel={
                            data
                                ? buildMomentumLabel(
                                      Math.floor(data.recent.timeSavedIntakeMinutes / 60),
                                      'hrs',
                                  )
                                : null
                        }
                        loading={isLoading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} xl={3}>
                    <HeroCard
                        title="MINUTES SAVED"
                        value={isLoading ? null : (data?.allTime.timeSavedConnectMinutes ?? 0)}
                        subtitle="· 30 min per matter autofilled"
                        tooltip="Each matter autofilled into your conveyancing software saves ~30 minutes of retyping."
                        isConnect
                        momentumLabel={
                            data
                                ? buildMomentumLabel(data.recent.timeSavedConnectMinutes, 'min')
                                : null
                        }
                        loading={isLoading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} xl={3}>
                    <HeroCard
                        title="INTAKES COMPLETED"
                        value={isLoading ? null : (data?.allTime.intakesCompleted ?? 0)}
                        subtitle="all time"
                        milestoneType="intakes"
                        momentumLabel={
                            data ? buildMomentumLabel(data.recent.intakesCompleted, '') : null
                        }
                        loading={isLoading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} xl={3}>
                    <HeroCard
                        title="IDENTITIES VERIFIED"
                        value={isLoading ? null : (data?.allTime.identitiesVerified ?? 0)}
                        subtitle="all time"
                        momentumLabel={
                            data ? buildMomentumLabel(data.recent.identitiesVerified, '') : null
                        }
                        loading={isLoading}
                    />
                </Grid>
            </Grid>

            {/* Needs attention strip */}
            {isLoading ? (
                <Skeleton variant="rounded" height={80} />
            ) : showNeedsAttention && data ? (
                <NeedsAttentionStrip data={data.needsAttention} />
            ) : null}

            {/* Your last 90 days */}
            <Typography variant="h6" sx={{ mt: 1 }}>
                Your last 90 days
            </Typography>

            {isLoading ? (
                <Grid container spacing={2}>
                    {[0, 1, 2, 3].map((i) => (
                        <Grid key={i} item xs={12} md={6}>
                            <Skeleton variant="rounded" height={160} />
                        </Grid>
                    ))}
                </Grid>
            ) : isNewFirm ? (
                // Onboarding card
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">Welcome to Lexo!</Typography>
                    <Typography sx={{ color: theme.palette.text.secondary, maxWidth: 480 }}>
                        Send your first intake to start saving time. Once you've sent a few intakes,
                        trends appear here — insights unlock at 5 completed intakes.
                    </Typography>
                    <Button variant="contained" size="large" onClick={() => navigate('/prospects')}>
                        Send your first intake →
                    </Button>
                </Box>
            ) : !hasInsightData ? (
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                    }}
                >
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                        Send a few more intakes and we'll show you trends here. Insights unlock at 5
                        completed intakes.
                    </Typography>
                </Box>
            ) : data ? (
                <Grid container spacing={2}>
                    {/* Typical time to completion */}
                    <Grid item xs={12} md={6}>
                        <InsightCard title="TYPICAL TIME TO COMPLETION">
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                {humanizeMinutes(data.trailing90.medianCompletionMinutes)}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                median, from the moment you send to client completion.
                            </Typography>
                        </InsightCard>
                    </Grid>

                    {/* Best day to send */}
                    <Grid item xs={12} md={6}>
                        <InsightCard title="BEST DAY TO SEND">
                            {data.trailing90.bestSendDay ? (
                                <>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {data.trailing90.bestSendDay.day}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: theme.palette.text.secondary }}
                                    >
                                        {Math.round(
                                            data.trailing90.bestSendDay.completionRate * 100,
                                        )}
                                        % completion rate
                                        {data.trailing90.bestSendDay.medianCompletionMinutes != null
                                            ? ` · fastest too (${humanizeMinutes(data.trailing90.bestSendDay.medianCompletionMinutes)} median)`
                                            : ''}
                                        {' · '}based on {data.trailing90.bestSendDay.sends} sends.
                                    </Typography>
                                </>
                            ) : (
                                <Typography
                                    variant="caption"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    Not enough data yet — need at least 5 sends on a single day.
                                </Typography>
                            )}
                        </InsightCard>
                    </Grid>

                    {/* When you send */}
                    <Grid item xs={12} md={6}>
                        <InsightCard title="WHEN YOU SEND">
                            <DayOfWeekBars
                                days={data.trailing90.sendsByDayOfWeek}
                                bestDay={data.trailing90.bestSendDay?.day ?? null}
                            />
                        </InsightCard>
                    </Grid>

                    {/* Fastest forms */}
                    <Grid item xs={12} md={6}>
                        <InsightCard title="FASTEST FORMS">
                            {data.trailing90.fastestForms.length === 0 ? (
                                <Typography
                                    variant="caption"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    Need at least 3 completions per template to show rankings.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {data.trailing90.fastestForms.map((form, i) => (
                                        <Box
                                            key={form.quoteTemplateId}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {i + 1}. {form.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    flexShrink: 0,
                                                    ml: 1,
                                                }}
                                            >
                                                {humanizeMinutes(form.medianCompletionMinutes)} (
                                                {form.completedCount})
                                            </Typography>
                                        </Box>
                                    ))}
                                    <Typography
                                        variant="caption"
                                        sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                                    >
                                        median completion (count)
                                    </Typography>
                                </Box>
                            )}
                        </InsightCard>
                    </Grid>
                </Grid>
            ) : null}
        </PageWrapper>
    );
}

function InsightCard({ title, children }: { title: string; children: ReactNode }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                height: '100%',
                boxSizing: 'border-box',
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.2,
                    fontSize: '10px',
                    letterSpacing: '0.08em',
                }}
            >
                {title}
            </Typography>
            {children}
        </Box>
    );
}
